import {
  IconDotsVertical,
  IconFile,
  IconFileTypeDoc,
  IconFileTypePdf,
  IconFileTypeXls,
  IconFolderFilled,
  IconSquare,
  IconSquareCheckFilled,
} from "@tabler/icons-react";
import clsx from "clsx";
import type { ComponentType, MouseEvent } from "react";

import type { FileManagerItem } from "@/components/core/FileManager";
import { useOptionalFileManagerUI } from "@/components/core/FileManager";

import styles from "./FileManagerItemCard.module.scss";

interface FileManagerItemCardProps {
  item: FileManagerItem;
  showCheckboxes?: boolean;
  onOpen?: (item: FileManagerItem) => void;
  /** Overrides the derived "Folder • N items" / file-size line. */
  subtitle?: string;
  /** The ⋮ button drives the shared context menu, so hide it where none is mounted. */
  showMenu?: boolean;
  wrapSubtitle?: boolean;
}

export interface FileIconConfig {
  Icon: ComponentType<{ size?: number; color?: string }>;
  color: string;
}

export function getIconConfig(item: FileManagerItem): FileIconConfig {
  if (item.type === "FOLDER")
    return { Icon: IconFolderFilled, color: "var(--color-light-indigo-800)" };

  const ct = item.contentType ?? "";
  const ext = item.name.split(".").pop()?.toLowerCase() ?? "";

  if (ct.includes("pdf") || ext === "pdf")
    return { Icon: IconFileTypePdf, color: "var(--color-light-red-800)" };
  if (ct.includes("excel") || ct.includes("spreadsheet") || ext === "xls" || ext === "xlsx")
    return { Icon: IconFileTypeXls, color: "var(--color-light-jade-700)" };
  if (ct.includes("word") || ct.includes("document") || ext === "doc" || ext === "docx")
    return { Icon: IconFileTypeDoc, color: "var(--color-light-blue-1000)" };

  return { Icon: IconFile, color: "var(--neutral-800)" };
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getSubtitle(item: FileManagerItem): string {
  if (item.type === "FOLDER") {
    const count = item.childCount ?? 0;
    return `Folder • ${count} ${count === 1 ? "item" : "items"}`;
  }
  if (item.fileSize != null) return formatFileSize(item.fileSize);
  return "—";
}

export function FileManagerItemCard({
  item,
  showCheckboxes,
  onOpen,
  subtitle: subtitleOverride,
  showMenu = true,
  wrapSubtitle = false,
}: FileManagerItemCardProps) {
  const { Icon, color } = getIconConfig(item);
  const subtitle = subtitleOverride ?? getSubtitle(item);

  const ui = useOptionalFileManagerUI();
  const selected = ui?.isSelected(item.id) ?? false;

  function handleMenuClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.dispatchEvent(
      new MouseEvent("contextmenu", {
        bubbles: true,
        cancelable: true,
        clientX: rect.left,
        clientY: rect.bottom,
      }),
    );
  }

  function handleClick(e: MouseEvent<HTMLDivElement>) {
    if (ui && (ui.isSelectionMode || e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      ui.toggleItem(item);
      return;
    }
    onOpen?.(item);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen?.(item);
    }
  }

  return (
    <div
      className={clsx(styles.card, selected && styles.selected)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={item.name}
    >
      <div className={styles.preview}>
        <div className={styles.iconWrapper}>
          <Icon size={40} color={color} />
        </div>
        {showCheckboxes && (
          <div className={clsx(styles.checkbox, selected && styles.checkboxVisible)}>
            {selected ? (
              <IconSquareCheckFilled
                size={18}
                color="#228be6"
                onClick={() => ui?.toggleItem(item)}
              />
            ) : (
              <IconSquare size={18} color="#adb5bd" onClick={() => ui?.toggleItem(item)} />
            )}
          </div>
        )}
      </div>
      <div className={styles.content}>
        <div className={styles.bodyContent}>
          <p className={styles.name}>{item.name}</p>
          <p className={clsx(styles.subtitle, wrapSubtitle && styles.subtitleWrap)}>{subtitle}</p>
        </div>
        {showMenu && (
          <button
            type="button"
            className={styles.menuBtn}
            onClick={handleMenuClick}
            aria-label="More options"
          >
            <IconDotsVertical size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
